import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import { NpsCalculatorService, NpsResult, NPS_CONSTRAINTS } from './nps-calculator.service';

@Component({
  selector: 'app-nps-calculator',
  standalone: true,
  imports: [FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './nps-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./nps-calculator.scss']
})
export class NpsCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(NpsCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = NPS_CONSTRAINTS;
  readonly Math = Math;

  currentAge = 30;
  retirementAge = 60;
  monthlyContribution = 10_000;
  annualRate = 10;
  annuityPercent = 40;
  annuityRate = 6;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     NpsResult | null = null;

  get tenureYears(): number { return this.retirementAge - this.currentAge; }

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.currentAge, this.C.age.min, this.C.age.max, 'Current age');
    if (e1) errors['currentAge'] = e1;
    const e2 = validateRange(this.retirementAge, this.C.age.min, 60, 'Retirement age');
    if (e2) errors['retirementAge'] = e2;
    else if (this.retirementAge <= this.currentAge) errors['retirementAge'] = 'Retirement age must be greater than current age.';
    const e3 = validateRange(this.monthlyContribution, this.C.monthlyContribution.min, this.C.monthlyContribution.max, 'Monthly contribution');
    if (e3) errors['monthlyContribution'] = e3;
    const e4 = validateRange(this.annualRate, this.C.rate.min, this.C.rate.max, 'Expected return');
    if (e4) errors['annualRate'] = e4;
    const e5 = validateRange(this.annuityPercent, this.C.annuityPercent.min, this.C.annuityPercent.max, 'Annuity allocation');
    if (e5) errors['annuityPercent'] = e5;
    const e6 = validateRange(this.annuityRate, this.C.annuityRate.min, this.C.annuityRate.max, 'Annuity rate');
    if (e6) errors['annuityRate'] = e6;
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'currentAge' || field === 'retirementAge') {
      (this as any)[field] = Math.floor((this as any)[field] || 0);
    }
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = {
      currentAge: true, retirementAge: true, monthlyContribution: true,
      annualRate: true, annuityPercent: true, annuityRate: true
    };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(
      Math.floor(this.currentAge), Math.floor(this.retirementAge), this.monthlyContribution,
      this.annualRate, this.annuityPercent, this.annuityRate
    );
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Total Invested', value: r.totalInvested, color: '#3b82f6' },
        { label: 'Wealth Gained', value: r.totalGain, color: '#10b981' }
      ], 'NPS Corpus');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.years.map(row => `Age ${row.age}`),
        [
          { label: 'Invested', data: r.years.map(row => Math.round(row.invested)), color: '#94a3b8', dashed: true },
          { label: 'Corpus Value', data: r.years.map(row => Math.round(row.balance)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.currentAge         = 30;
    this.retirementAge      = 60;
    this.monthlyContribution= 10_000;
    this.annualRate         = 10;
    this.annuityPercent     = 40;
    this.annuityRate        = 6;
    this.formErrors         = {};
    this.touched            = {};
    this.showFormErrors     = false;
    this.calculated         = false;
    this.result             = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
